import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import Bowser from 'bowser';

import { adviceRooms, guests } from 'api';
import { isStreamingEnabled } from 'helpers/features';
import { fetchAdviceRoom, fetchGuestAdviceRoom } from 'store/adviceRooms/actions';
import { RoomConnectionState } from 'components/VideoChat/RoomConnectionState';

import HandleAWSTranscribe from './handleAWSTranscribe';
import HandleWebSpeechAPI, { SpeechRecognition } from './handleWebSpeechAPI';
import type { SourceType, TranscriptionHandler } from './types';

enum TranscriptionMethod {
  AWSTranscribe,
  WEBSpeech,
}

interface Props {
  enabled: boolean;
  hasUserConsented: boolean;
  adviceRoomId?: number;
  connectionState: RoomConnectionState;
  isAudioEnabled: boolean;
  authToken?: string;
  selectedAudioDeviceId?: string;
}
export function useManageLiveTranscription({
  enabled,
  hasUserConsented,
  adviceRoomId,
  isAudioEnabled,
  authToken,
  connectionState,
  selectedAudioDeviceId,
}: Props) {
  const dispatch = useDispatch();
  const handler = useRef<TranscriptionHandler>();
  const hasJoinedOnce = useRef(false);

  const [handlerStatus, setHandlerStatus] = useState<TranscriptionMethod | null>(null);

  const sendToServer = useCallback(
    ({ end, text, source }: { end: number; text: string; source: SourceType }) => {
      if (!adviceRoomId) return;
      const timestamp = new Date();
      const startTime = timestamp.toISOString();

      timestamp.setSeconds(timestamp.getSeconds() + end);
      const endTime = timestamp.toISOString();

      const dataPayload = {
        segment: {
          text,
          startTime,
          endTime,
          source,
        },
      };
      if (authToken) {
        guests()
          .clientMeetingRooms()
          .sendTranscription({
            ...dataPayload,
            authToken,
          });
      } else {
        adviceRooms(adviceRoomId)
          .transcriptions()
          .POST({
            ...dataPayload,
          });
      }
    },
    [adviceRoomId, authToken]
  );

  useEffect(() => {
    const browser = Bowser.getParser(window.navigator.userAgent);
    const isValidBrowser = browser.satisfies({
      windows: {
        chrome: '>=139',
      },
      macos: {
        chrome: '>=139',
      },
      linux: {
        chrome: '>=139',
      },
    });

    if (isValidBrowser) {
      SpeechRecognition.available({ processLocally: true, langs: ['en-US'] }).then(
        (result: string) => {
          if (result === 'available') {
            handler.current = HandleWebSpeechAPI({ sendToServer });
            setHandlerStatus(TranscriptionMethod.WEBSpeech);
          } else {
            handler.current = HandleAWSTranscribe({ sendToServer, adviceRoomId, authToken });
            setHandlerStatus(TranscriptionMethod.AWSTranscribe);
          }
        }
      );
    } else {
      handler.current = HandleAWSTranscribe({ sendToServer, adviceRoomId, authToken });
      setHandlerStatus(TranscriptionMethod.AWSTranscribe);
    }
  }, [sendToServer, adviceRoomId, authToken]);

  useEffect(() => {
    if (authToken) {
      dispatch(fetchGuestAdviceRoom(authToken));
    } else if (adviceRoomId) {
      dispatch(fetchAdviceRoom(adviceRoomId));
    }
  }, [adviceRoomId, dispatch, authToken]);

  useEffect(() => {
    return () => handler.current?.cleanUpTranscription();
  }, []);

  useEffect(() => {
    if (!isStreamingEnabled()) return;
    if (handlerStatus === null || !handler.current) return;

    handler.current.initTranscribeHandler();
  }, [handlerStatus]);

  useEffect(() => {
    if (!isStreamingEnabled() || handlerStatus === null) return;
    if (!handler.current) return;

    if (
      enabled &&
      connectionState === RoomConnectionState.Connected &&
      isAudioEnabled &&
      hasUserConsented
    ) {
      handler.current.startTranscription(selectedAudioDeviceId);
      hasJoinedOnce.current = true;
    } else if (hasJoinedOnce.current) handler.current.stopTranscription();
  }, [
    handlerStatus,
    enabled,
    connectionState,
    isAudioEnabled,
    hasUserConsented,
    selectedAudioDeviceId,
  ]);
}
